import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "@mui/material";
import {
  BufferService,
  LoadingContext,
  NavigateContext,
  M_Button,
  M_Header,
} from "src/M_Components";
import { History } from "@mui/icons-material";
import { QuoteInfo } from "./components/QuoteInfo";
import { QuoteDates } from "./components/QuoteDates";
import { QuotePorts } from "./components/QuotePorts";
import { QuoteEntities } from "./components/QuoteEntities";
import { QuoteEquipment } from "./components/QuoteEquiment";
import { QuoteGoods } from "./components/QuoteGoods";
import { QuoteCalculator } from "./components/QuoteCalculator";
import { QuoteCharges } from "./components/QuoteCharges";
import { getQuoteApiErrorMessage } from "./utils/quoteApiError";
import {
  buildRevisionChangedFields,
  normalizeQuoteGetResponse,
} from "./utils/quoteRevisionHelpers";
import {
  QuoteEquipmentGoodsSection,
  QuoteInfoDatesPortsSection,
} from "./components/QuotePageSections";

export const QuoteRevisionDetail = () => {
  const [id] = useState(sessionStorage.getItem("selectedTab"));
  const { t } = useTranslation();
  const ctx = useContext(LoadingContext);
  const navigateCtx = useContext(NavigateContext);
  const [data, setData] = useState({ isRevisionView: true });
  const [compareRevision, setCompareRevision] = useState(null);

  const quoteGetRequestService = new BufferService("quotegetrequest");

  const matches11 = useMediaQuery("(min-width:970px)");
  const matches12 = useMediaQuery("(min-width:930px)");

  useEffect(() => {
    loadRevision();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadRevision = async () => {
    const row = ctx.passValue.get("Row");
    const previousRow = ctx.passValue.get("PreviousRevisionRow");
    const revisionId = row?.Id;

    if (!revisionId) {
      ctx.toastMessages("error", "", t("SelectedLine"));
      return;
    }

    ctx.setIsLoad(true);
    try {
      const response = await quoteGetRequestService.getId(revisionId);
      if (response?.name === "AxiosError") {
        ctx.toastMessages(
          "error",
          t("Error"),
          getQuoteApiErrorMessage(response) || t("Error"),
        );
        return;
      }

      const normalized = normalizeQuoteGetResponse(response);

      if (previousRow?.Id) {
        const previousResponse = await quoteGetRequestService.getId(previousRow.Id);
        if (previousResponse?.name !== "AxiosError") {
          const previousNormalized = normalizeQuoteGetResponse(previousResponse);
          const changedFields = buildRevisionChangedFields(
            normalized,
            previousNormalized,
          );
          setCompareRevision(previousNormalized.revision);
          setData({ ...normalized, revisionChangedFields: changedFields });
          return;
        }
      }

      setCompareRevision(null);
      setData({ ...normalized, revisionChangedFields: [] });
    } catch (err) {
      ctx.toastMessages("error", err.message, t("Error"));
    } finally {
      ctx.setIsLoad(false);
    }
  };

  return (
    <div>
      <M_Header
        element={
          <>
            <M_Button
              tooltip={t("Back")}
              label={t("Back")}
              icon={
                <i
                  className="pi pi-arrow-left iconTable"
                  style={{
                    width: "20px",
                    color: ctx.secondaryColor
                      ? ctx.secondaryColor
                      : "var(--color-black)",
                  }}
                />
              }
              onClick={() => navigateCtx.closeTab(id?.replace(/"/g, ""))}
            />
            <M_Button
              tooltip={t("ViewRevision")}
              icon={<History />}
              label={`${t("Revision")} ${data.revision ?? ""}`}
              onClick={() => {}}
            />
          </>
        }
      />
      <div style={{ margin: "20px" }}>
        {compareRevision != null && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "16px",
              padding: "10px 14px",
              borderRadius: "8px",
              backgroundColor: "var(--color-grey-light)",
              fontSize: "14px",
            }}
          >
            <History fontSize="small" />
            <span>
              {data.revisionChangedFields?.length
                ? t("RevisionChangesHint", { revision: compareRevision })
                : t("NoChangesFromPrevious")}
            </span>
          </div>
        )}

        <QuoteInfoDatesPortsSection
          wide={matches11}
          quoteInfo={
            <QuoteInfo
              dataState={data}
              setDataState={setData}
              editMode
            />
          }
          dates={<QuoteDates dataState={data} setDataState={setData} hideDueDate />}
          ports={<QuotePorts dataState={data} setDataState={setData} />}
        />
        <div>
          <QuoteEntities dataState={data} setDataState={setData} />
        </div>
        <QuoteEquipmentGoodsSection
          wide={matches12}
          equipment={<QuoteEquipment dataState={data} setDataState={setData} />}
          goods={<QuoteGoods dataState={data} setDataState={setData} />}
        />
        <QuoteCharges dataState={data} setDataState={setData} />
        <div style={{ margin: "25px" }} />
        <QuoteCalculator dataState={data} setDataState={setData} />
      </div>
    </div>
  );
};
